import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  downloadAccountingSourceDataExport,
  getAccountingSourceData,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell, { AccountingCard } from "./SalonBusinessCpaShell";

function toIsoDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    start_date: toIsoDate(start),
    end_date: toIsoDate(end),
  };
}

function money(value) {
  const number = Number(value || 0);
  return number.toLocaleString("en-CA", {
    style: "currency",
    currency: "CAD",
  });
}

function numberLabel(value, options = {}) {
  const number = Number(value || 0);
  return number.toLocaleString("en-CA", options);
}

function sourceParams(range) {
  return {
    start_date: range.start_date,
    end_date: range.end_date,
  };
}

function filenameFromDisposition(header, fallback) {
  const match = String(header || "").match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

export default function SalonBusinessCpaSourceData() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const initialRange = useMemo(() => {
    const fallback = defaultDateRange();
    return {
      start_date: searchParams.get("start_date") || fallback.start_date,
      end_date: searchParams.get("end_date") || fallback.end_date,
    };
  }, [searchParams]);
  const [range, setRange] = useState(initialRange);
  const [draftRange, setDraftRange] = useState(initialRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [error, setError] = useState("");

  const params = useMemo(() => sourceParams(range), [range]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const nextData = await getAccountingSourceData(token, params);
        if (!cancelled) setData(nextData);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail ||
              "Unable to load accounting source data right now."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [params, token]);

  const handleRangeSubmit = (event) => {
    event.preventDefault();
    setRange(draftRange);
  };

  const handleExport = async (exportType = "bookkeeping_review") => {
    if (!token) return;
    setExporting(exportType);
    setError("");
    try {
      const response = await downloadAccountingSourceDataExport(token, {
        ...params,
        export_type: exportType,
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filenameFromDisposition(
        response.headers?.["content-disposition"],
        data?.export_filename || "salonbusinesscpa_source_data.csv"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Unable to export this source data right now."
      );
    } finally {
      setExporting("");
    }
  };

  const sales = data?.sales_totals || {};
  const payroll = data?.payroll_summary || {};
  const hasPaymentRows = Boolean(data?.payment_methods?.length);
  const hasStaffRows = Boolean(data?.staff_activity?.length);
  const exportTypes = Array.isArray(data?.export_type_options)
    ? data.export_type_options
    : [
        { code: "gst_hst_review", label: "GST/HST review" },
        { code: "bookkeeping_review", label: "Bookkeeping review" },
        { code: "payroll_review", label: "Payroll review" },
        { code: "year_end_tax_package", label: "Year-end tax package" },
      ];

  return (
    <SalonBusinessCpaShell
      title="Accounting source data"
      description="Review sales, tax collected, payment methods, and staff payroll activity from Ellyanna Booking for the period your accountant is working on."
      actions={
        <div className="flex flex-wrap gap-2">
          {exportTypes.map((type) => (
            <button
              key={type.code}
              type="button"
              onClick={() => handleExport(type.code)}
              disabled={loading || Boolean(exporting) || !data}
              className="inline-flex min-h-10 items-center rounded-lg bg-rose-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {exporting === type.code ? "Preparing..." : type.label}
            </button>
          ))}
        </div>
      }
    >
      <form
        onSubmit={handleRangeSubmit}
        className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_1fr_auto]"
      >
        <label className="text-sm font-semibold text-slate-800">
          Start date
          <input
            type="date"
            value={draftRange.start_date}
            onChange={(event) =>
              setDraftRange((current) => ({
                ...current,
                start_date: event.target.value,
              }))
            }
            className="mt-2 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900"
          />
        </label>
        <label className="text-sm font-semibold text-slate-800">
          End date
          <input
            type="date"
            value={draftRange.end_date}
            onChange={(event) =>
              setDraftRange((current) => ({
                ...current,
                end_date: event.target.value,
              }))
            }
            className="mt-2 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900"
          />
        </label>
        <button
          type="submit"
          className="min-h-11 self-end rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
        >
          Update view
        </button>
      </form>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
          Loading source data...
        </div>
      ) : (
        <>
          <section className="grid gap-4 md:grid-cols-4">
            <AccountingCard title="Sales before tax" body="Closed ticket sales for the selected dates.">
              <p className="text-2xl font-semibold text-slate-950">
                {money(sales.sales_before_tax)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {numberLabel(sales.ticket_count)} closed tickets
              </p>
            </AccountingCard>
            <AccountingCard title="Tax collected" body="Sales tax shown on closed tickets.">
              <p className="text-2xl font-semibold text-slate-950">
                {money(sales.tax_collected)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {numberLabel(sales.tax_exempt_ticket_count)} tax-exempt tickets
              </p>
            </AccountingCard>
            <AccountingCard title="Payments received" body="Payments recorded in Ellyanna Booking.">
              <p className="text-2xl font-semibold text-slate-950">
                {money(sales.amount_paid)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Tips: {money(sales.tips)}
              </p>
            </AccountingCard>
            <AccountingCard title="Payroll activity" body="Saved payroll runs overlapping this date range.">
              <p className="text-2xl font-semibold text-slate-950">
                {money(payroll.total_pay)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {numberLabel(payroll.run_count)} payroll runs
              </p>
            </AccountingCard>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  Sales by day
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Daily sales, payments, tips, and tax for the selected range.
                </p>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Tickets</th>
                    <th className="py-3 pr-4">Sales</th>
                    <th className="py-3 pr-4">Tax</th>
                    <th className="py-3 pr-4">Payments</th>
                    <th className="py-3 pr-4">Tips</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data?.sales_by_day?.length ? (
                    data.sales_by_day.map((row) => (
                      <tr key={row.date}>
                        <td className="py-3 pr-4 font-semibold text-slate-900">{row.date}</td>
                        <td className="py-3 pr-4 text-slate-600">{numberLabel(row.ticket_count)}</td>
                        <td className="py-3 pr-4 text-slate-600">{money(row.sales_before_tax)}</td>
                        <td className="py-3 pr-4 text-slate-600">{money(row.tax_collected)}</td>
                        <td className="py-3 pr-4 text-slate-600">{money(row.amount_paid)}</td>
                        <td className="py-3 pr-4 text-slate-600">{money(row.tips)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-5 text-sm text-slate-500">
                        No daily sales found for this date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  Payment deposits
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Compare daily deposits, card batches, cash, gift cards, and other payment records.
                </p>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Method</th>
                    <th className="py-3 pr-4">Payments</th>
                    <th className="py-3 pr-4">Amount</th>
                    <th className="py-3 pr-4">Tips</th>
                    <th className="py-3 pr-4">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data?.payment_deposits?.length ? (
                    data.payment_deposits.map((row) => (
                      <tr key={`${row.date}-${row.method}`}>
                        <td className="py-3 pr-4 font-semibold text-slate-900">
                          {row.date}
                        </td>
                        <td className="py-3 pr-4 font-semibold text-slate-900">
                          {row.method_label}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {numberLabel(row.payment_count)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.amount)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.tips)}
                        </td>
                        <td className="py-3 pr-4 font-semibold text-slate-900">
                          {money(row.total_with_tips)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-5 text-sm text-slate-500">
                        No payment records found for this date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">
              Taxes by rate
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Use this for GST/HST review and to check tax collected by rate.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-3 pr-4">Rate</th>
                    <th className="py-3 pr-4">Items</th>
                    <th className="py-3 pr-4">Taxable sales</th>
                    <th className="py-3 pr-4">Tax collected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data?.taxes_by_rate?.length ? (
                    data.taxes_by_rate.map((row) => (
                      <tr key={row.rate_label}>
                        <td className="py-3 pr-4 font-semibold text-slate-900">{row.rate_label}</td>
                        <td className="py-3 pr-4 text-slate-600">{numberLabel(row.item_count)}</td>
                        <td className="py-3 pr-4 text-slate-600">{money(row.taxable_sales)}</td>
                        <td className="py-3 pr-4 font-semibold text-slate-900">{money(row.tax_collected)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-5 text-sm text-slate-500">
                        No tax-rate details found for this date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">
              Payment methods
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Summary by payment method for bookkeeping review.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-3 pr-4">Method</th>
                    <th className="py-3 pr-4">Payments</th>
                    <th className="py-3 pr-4">Amount</th>
                    <th className="py-3 pr-4">Tips</th>
                    <th className="py-3 pr-4">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {hasPaymentRows ? (
                    data.payment_methods.map((row) => (
                      <tr key={row.method}>
                        <td className="py-3 pr-4 font-semibold text-slate-900">
                          {row.method_label}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {numberLabel(row.payment_count)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.amount)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.tips)}
                        </td>
                        <td className="py-3 pr-4 font-semibold text-slate-900">
                          {money(row.total_with_tips)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-5 text-sm text-slate-500">
                        No payment records found for this date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">
              Staff and payroll-related activity
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Review service sales, product sales, tips, and saved payroll totals by staff member.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-3 pr-4">Staff</th>
                    <th className="py-3 pr-4">Service sales</th>
                    <th className="py-3 pr-4">Product sales</th>
                    <th className="py-3 pr-4">Tax</th>
                    <th className="py-3 pr-4">Tips</th>
                    <th className="py-3 pr-4">Payroll pay</th>
                    <th className="py-3 pr-4">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {hasStaffRows ? (
                    data.staff_activity.map((row) => (
                      <tr key={`${row.staff_id || "staff"}-${row.staff_name}`}>
                        <td className="py-3 pr-4">
                          <div className="font-semibold text-slate-900">
                            {row.staff_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {numberLabel(row.ticket_count)} tickets / {numberLabel(row.item_count)} items
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.service_sales)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.retail_sales)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.tax_collected)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {money(row.tips)}
                        </td>
                        <td className="py-3 pr-4 font-semibold text-slate-900">
                          {money(row.payroll_total_pay)}
                        </td>
                        <td className="py-3 pr-4 text-slate-600">
                          {numberLabel(row.payroll_hours, {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="py-5 text-sm text-slate-500">
                        No staff sales or payroll activity found for this date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Tips by staff</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-3 pr-4">Staff</th>
                      <th className="py-3 pr-4">Entries</th>
                      <th className="py-3 pr-4">Tips</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data?.tips_by_staff?.length ? (
                      data.tips_by_staff.map((row) => (
                        <tr key={`${row.staff_id || "staff"}-${row.staff_name}`}>
                          <td className="py-3 pr-4 font-semibold text-slate-900">{row.staff_name}</td>
                          <td className="py-3 pr-4 text-slate-600">{numberLabel(row.tip_count)}</td>
                          <td className="py-3 pr-4 text-slate-600">{money(row.tips)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="py-5 text-sm text-slate-500">
                          No tips found for this date range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Payroll activity by staff</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-3 pr-4">Staff</th>
                      <th className="py-3 pr-4">Runs</th>
                      <th className="py-3 pr-4">Hours</th>
                      <th className="py-3 pr-4">Total pay</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data?.payroll_by_staff?.length ? (
                      data.payroll_by_staff.map((row) => (
                        <tr key={`${row.staff_id || "payroll"}-${row.staff_name}`}>
                          <td className="py-3 pr-4 font-semibold text-slate-900">{row.staff_name}</td>
                          <td className="py-3 pr-4 text-slate-600">{numberLabel(row.payroll_runs)}</td>
                          <td className="py-3 pr-4 text-slate-600">{numberLabel(row.payroll_hours, { maximumFractionDigits: 2 })}</td>
                          <td className="py-3 pr-4 text-slate-600">{money(row.payroll_total_pay)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="py-5 text-sm text-slate-500">
                          No payroll activity found for this date range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      )}
    </SalonBusinessCpaShell>
  );
}
